import {fileKey, imageClaims, portraitCandidate, qualifyPortrait} from "./portrait-policy.js";
import {createCatalogLoader} from "./portrait-catalog.js";
import {photographEvidence,producerCategories,nonPortraitMedium,photographCategory} from "./portrait-photography.js";
import {plainText} from "./portrait-policy.js";
export async function fetchPortrait(person, {fetcher = fetch, signal} = {}) {
  const qid = person.wikidataId;
  const unavailable = reason => ({status:"unavailable", reason, qid});
  if (!/^Q[1-9]\d*$/.test(qid || "")) return unavailable("no-wikidata");
  let wiki;
  try { wiki = new URL(person.wikipediaUrl); } catch { return unavailable("no-article"); }
  if (wiki.protocol !== "https:" || !["fr.wikipedia.org","en.wikipedia.org"].includes(wiki.hostname) || !wiki.pathname.startsWith("/wiki/") || wiki.username || wiki.password) return unavailable("invalid-article");
  async function api(host, params) {
    const url = new URL(`https://${host}/w/api.php`);
    url.search = new URLSearchParams({...params, format:"json", origin:"*", maxlag:"5"});
    const response = await fetcher(url.href, {signal, credentials:"omit", referrerPolicy:"no-referrer"});
    if (!response.ok) throw new Error(`Wikimedia HTTP ${response.status}`);
    const data = await response.json();
    if (data.error) throw new Error("Wikimedia unavailable: " + data.error.code);
    return data;
  }
  const articleData = await api(wiki.hostname, {action:"query", titles:decodeURIComponent(wiki.pathname.slice(6)).replace(/_/g," "), redirects:"1", prop:"pageprops|pageimages", piprop:"name", pilicense:"free"});
  const article = Object.values(articleData.query?.pages || {})[0];
  if (article?.pageprops?.wikibase_item !== qid) return unavailable("identity-mismatch");
  // The exact biography QID already binds its chosen image to this person.
  // A Wikidata outage must not hide an otherwise usable Wikipedia portrait.
  const entity = article.pageimage ? null : (await api("www.wikidata.org", {action:"wbgetentities", ids:qid, props:"claims|labels", languages:"fr|en"})).entities?.[qid];
  const claims = imageClaims(entity);
  const file = portraitCandidate(entity, article);
  if (!file) return unavailable(claims.length?"image-not-corroborated":"no-image");
  const commons = await api("commons.wikimedia.org", {action:"query", titles:file, prop:"imageinfo", iiprop:"url|extmetadata|sha1|mime|size|metadata", iiurlwidth:"160", iiextmetadatalanguage:"en"});
  const page = Object.values(commons.query?.pages || {})[0];
  if (!page?.pageid || !page.imageinfo) return unavailable("no-commons-file");
  const mid = "M" + page.pageid;
  const articleLead = fileKey(article.pageimage) === fileKey(page.title);
  const media = articleLead ? null : (await api("commons.wikimedia.org", {action:"wbgetentities", ids:mid, props:"claims"})).entities?.[mid];
  const info=page.imageinfo[0],text=key=>plainText(info.extmetadata?.[key]?.value);
  if(!articleLead&&!photographEvidence(info,media,text)&&!nonPortraitMedium(text("Categories"))){
    const cats=producerCategories(text("Categories"));
    if(cats.length){
      const parents=await api("commons.wikimedia.org",{action:"query",titles:cats.map(c=>"Category:"+c).join("|"),prop:"categories",cllimit:"500"});
      // Omission under continuation is conservative, never inferred evidence.
      info.photographicCategoryEvidence=Object.values(parents.query?.pages||{}).flatMap(p=>(p.categories||[]).filter(c=>photographCategory(c.title)).map(c=>({category:p.title.replace(/^Category:/,""),parent:c.title})));
    }
  }
  const record=qualifyPortrait({qid,entity,article,page,media});
  if(record.evidence)Object.assign(record.evidence,{articleUrl:wiki.href,wikidataUrl:`https://www.wikidata.org/wiki/${qid}`});
  return record;
}

// Metadata and decoded pixels have independent budgets. Only settled, accepted
// viewport symbols may acquire pixels. Moving away aborts work, not identity.
export function createPortraitStore(onChange, {
  loader = createCatalogLoader(fetchPortrait), imageFactory = () => new Image(),
  bitmapFactory = typeof createImageBitmap === "function" ? createImageBitmap : null,
  limit = 96, byteLimit = 8 * 1024 * 1024, metadataLimit = 512,
  metadataByteLimit = 2 * 1024 * 1024, concurrency = 2, settleMs = 140,
  now = () => performance.now(),
} = {}) {
  const metadata = new Map(), images = new Map(), active = new Map();
  let wanted = new Map(), killed = false, timer = null, decodedBytes = 0, metadataBytes = 0;
  const counters = {metadataRequests:0,imageRequests:0,cancelled:0,evictions:0,peakDecodedBytes:0};
  const release = item => {
    if(item?.image?.close)item.image.close();
    else if(item?.image && "src" in item.image)item.image.src="";
    else if(item?.image?.getContext){item.image.width=0;item.image.height=0;}
  };
  function evict(qid) { const item=images.get(qid);if(!item)return;decodedBytes-=item.bytes;release(item);images.delete(qid);counters.evictions++; }
  function remember(qid,record) {
    metadataBytes-=metadata.get(qid)?.bytes||0;
    const bytes=new TextEncoder().encode(JSON.stringify(record)).length;
    metadata.delete(qid);metadata.set(qid,{record,bytes});metadataBytes+=bytes;
    while(metadata.size>metadataLimit||metadataBytes>metadataByteLimit){
      const [key,item]=metadata.entries().next().value;
      // Pixels must never outlive their verified attribution/identity record.
      evict(key);metadata.delete(key);metadataBytes-=item.bytes;
    }
  }
  function schedule(delay=settleMs) { if(!killed&&timer===null)timer=setTimeout(()=>{timer=null;pump();},delay); }
  async function decode(record,signal) {
    const image=imageFactory();image.crossOrigin="anonymous";image.referrerPolicy="no-referrer";image.decoding="async";
    try {
      counters.imageRequests++;
      await new Promise((resolve,reject)=>{
        const cancel=()=>{image.src="";reject(new Error("aborted"));};
        const finish=fn=>()=>{signal.removeEventListener("abort",cancel);fn();};
        image.onload=finish(resolve);image.onerror=finish(()=>reject(new Error("image unavailable")));
        signal.addEventListener("abort",cancel,{once:true});image.src=record.imageUrl;
      });
      if(signal.aborted)throw new Error("aborted");
      const width=image.naturalWidth,height=image.naturalHeight;
      // Never retain an accidentally delivered original or unbounded decoder.
      if(!width||!height||width*height>1024*1024)throw new Error("oversized thumbnail");
      const scale=Math.min(1,160/Math.max(width,height)),w=Math.max(1,Math.round(width*scale)),h=Math.max(1,Math.round(height*scale));
      let result;
      if(bitmapFactory)result=await bitmapFactory(image,{resizeWidth:w,resizeHeight:h,resizeQuality:"high"});
      else if(typeof document!=="undefined"){result=document.createElement("canvas");result.width=w;result.height=h;result.getContext("2d").drawImage(image,0,0,w,h);}
      else return {image,bytes:width*height*4}; // Injected test/non-DOM fallback.
      image.src="";
      if(signal.aborted){result.close?.();throw new Error("aborted");}
      return {image:result,bytes:w*h*4};
    } catch(error) {image.src="";throw error;}
  }
  function pump() {
    if(killed)return;
    let wait=false;
    for(const [qid,entry] of wanted) {
      if(active.size>=concurrency)break;
      if(images.has(qid)||active.has(qid))continue;
      const saved=metadata.get(qid)?.record;
      if(saved?.status==="unavailable"||saved?.retryAt>now())continue;
      if(now()-entry.since<settleMs){wait=true;continue;}
      const controller=new AbortController();active.set(qid,controller);
      const timeout=setTimeout(()=>controller.abort("timeout"),20000);
      (async()=>{
        try {
          let record=saved;
          if(record?.status!=="ready"){counters.metadataRequests++;record=await loader(entry.person,{signal:controller.signal});if(controller.signal.aborted)throw new Error("aborted");remember(qid,record);}
          if(record.status==="ready"&&wanted.has(qid)) {
            const decoded=await decode(record,controller.signal);
            if(killed||controller.signal.aborted||!wanted.has(qid)){release(decoded);return;}
            const current=metadata.get(qid)?.record;
            // Another acquisition can evict metadata while this bitmap is being
            // decoded. Never publish pixels without their matching live record.
            if(current?.status!=="ready"||current.sha1!==record.sha1||current.imageUrl!==record.imageUrl){release(decoded);return;}
            // Evict offscreen first, then LRU. Cache never exceeds either bound.
            while(images.size>=limit||decodedBytes+decoded.bytes>byteLimit){const key=[...images.keys()].find(k=>!wanted.has(k))||images.keys().next().value;if(!key)break;evict(key);}
            if(decoded.bytes<=byteLimit){images.set(qid,decoded);decodedBytes+=decoded.bytes;counters.peakDecodedBytes=Math.max(counters.peakDecodedBytes,decodedBytes);}else release(decoded);
          }
        } catch {
          // Cancellation is not a negative identity result. Transient transport
          // failures have a cooldown, not a session-long "no portrait" verdict.
          if(!killed && (!controller.signal.aborted||controller.signal.reason==="timeout")){
            const old=metadata.get(qid)?.record;
            remember(qid,{...old,status:old?.status==="ready"?"ready":"error",reason:"source-unavailable",retryAt:now()+60000});
          }
        } finally {clearTimeout(timeout);active.delete(qid);if(!killed){onChange(qid);schedule(0);}}
      })();
    }
    if(wait)schedule();
  }
  return {
    get(qid){
      const item=images.get(qid),saved=metadata.get(qid),record=saved?.record;
      if(saved){metadata.delete(qid);metadata.set(qid,saved);}
      if(item){images.delete(qid);images.set(qid,item);return {...record,status:"ready",image:item.image};}
      if(record?.status==="ready")return {...record,status:"loading"};
      return record||(active.has(qid)?{status:"loading"}:undefined);
    },
    request(people){
      const next=new Map();
      for(const person of people){const qid=person?.wikidataId;if(/^Q[1-9]\d*$/.test(qid||""))next.set(qid,{person,since:wanted.get(qid)?.since??now()});if(next.size>=limit)break;}
      wanted=next;
      for(const [qid,controller] of active)if(!wanted.has(qid)&&!controller.signal.aborted){counters.cancelled++;controller.abort("offscreen");}
      schedule();
    },
    stats(){return {...counters,active:active.size,pending:[...wanted.keys()].filter(q=>{const saved=metadata.get(q)?.record;return !images.has(q)&&!active.has(q)&&!["unavailable","error"].includes(saved?.status)&&!(saved?.retryAt>now());}).length,cached:metadata.size,ready:images.size,decodedBytes,byteLimit,metadataBytes,metadataByteLimit,wanted:[...wanted.keys()],activeIds:[...active.keys()]};},
    kill(){killed=true;clearTimeout(timer);wanted.clear();active.forEach(c=>c.abort("killed"));images.forEach(release);images.clear();metadata.clear();decodedBytes=metadataBytes=0;},
  };
}
